import React from 'react'
import cloneDeep from 'clone-deep'
import { t } from '../../../../config/i18n'
import { Checkbox, Form, FormInstance, Input, Modal, Select } from 'antd'
import { TExtraField } from '../../../../models/ExtraField'
import styles from './Modal.module.scss'
import { dynamicCatalogService } from '../../../../services/DynamicCatalogService'

interface Props {
  field: TExtraField
  onSave: (field: TExtraField) => void
  onCancel: () => void
}

export const ExtraFieldsSettingsModal = ({ field, onSave, onCancel }: Props) => {
  const formRef = React.useRef<FormInstance | null>(null)
  const [formValues, setFormValues] = React.useState<TObjectAny>(cloneDeep(field))
  const [catalogLoading, setCatalogLoading] = React.useState(false)
  const [catalogOptions, setCatalogOptions] = React.useState<TOption[]>([
    { value: field.catalogId, label: '' } // loading state
  ])

  React.useEffect(() => {
    if (formValues.fieldType === 'CATALOG') {
      setCatalogLoading(true)
      dynamicCatalogService.getConfigItems()
        .then(r => setCatalogOptions(r.map(i => ({ value: i.id, label: i.name }))))
        .catch(window.logger.error)
        .finally(() => setCatalogLoading(false))
    }
  }, [formValues.fieldType])

  const onValidate = React.useCallback(() => {
    formRef.current?.submit()
  }, [formRef])

  const onSubmit = React.useCallback((values: TObjectAny) => {
    onSave({ ...formValues, ...values, uiKey: field.uiKey } as TExtraField)
  }, [formValues])

  return (
    <Modal
      visible={true}
      title={t.catalog.fieldEdit}
      okText={t.common.save}
      cancelText={t.common.cancel}
      onOk={onValidate}
      onCancel={onCancel}
    >
      <div className={styles.root}>
        <Form
          ref={formRef}
          autoComplete="off"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          initialValues={formValues}
          onValuesChange={(_, values) => setFormValues(values)}
          onFinish={onSubmit}
        >
          <Form.Item
            label={t.common.name}
            name="name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Extra ID"
            name="extraId"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t.catalog.fieldType}
            name="fieldType"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="TEXT">text</Select.Option>
              <Select.Option value="TEXTAREA">text area</Select.Option>
              <Select.Option value="CHECKBOX">checkbox</Select.Option>
              <Select.Option value="CATALOG">catalog</Select.Option>
            </Select>
          </Form.Item>

          {formValues.fieldType === 'CATALOG' && (
            <Form.Item
              label={t.catalog.fieldCatalog}
              name="catalogId"
              rules={[{ required: true }]}
            >
              <Select
                loading={catalogLoading}
                options={catalogOptions}
              />
            </Form.Item>
          )}

          {formValues.fieldType === 'CATALOG' && (
            <Form.Item name="multiSelect" valuePropName="checked" wrapperCol={{ offset: 6, span: 18 }} style={{ margin: 0 }}>
              <Checkbox>{t.catalog.fieldMulti}</Checkbox>
            </Form.Item>
          )}

          <Form.Item name="required" valuePropName="checked" wrapperCol={{ offset: 6, span: 18 }} style={{ margin: 0 }}>
            <Checkbox>{t.catalog.fieldReq}</Checkbox>
          </Form.Item>

          <Form.Item name="unique" valuePropName="checked" wrapperCol={{ offset: 6, span: 18 }} style={{ margin: 0 }}>
            <Checkbox>{t.catalog.fieldUniq}</Checkbox>
          </Form.Item>

          <Form.Item name="displayInGrid" valuePropName="checked" wrapperCol={{ offset: 6, span: 18 }} style={{ margin: 0 }}>
            <Checkbox>{t.catalog.displayInGrid}</Checkbox>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  )
}
